import { useSubscription } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import type { DetailLevel, Format } from '@eluve/blocks';
import { WorkerStatusTypesLookup } from '@eluve/graphql-types';
import { graphql } from '@eluve/graphql.tada';
import { specSchema } from '@eluve/sdui-types';
import type { Spec } from '@eluve/sdui-types';

const cachedOutputSubscription = graphql(`
  subscription CachedOutputStatus($tenantId: uuid!, $id: uuid!) {
    cachedOutputsByPk(tenantId: $tenantId, id: $id) {
      __typename
      id
      status
      value
    }
  }
`);

interface UseInsightsPreviewCacheArgs {
  tenantId: string;
}

export interface PreviewSettings {
  appointmentId: string | null;
  format: Format;
  detailLevel: DetailLevel;
  customInstructions: string;
}

/**
 * Outcome of a `generate(settings)` call.
 *   - `completed`: 200 path — the spec is in cache and `lookup(settings)` returns it.
 *   - `pending`: 202 path — the worker is scheduled; the subscription will
 *     fill the cache when COMPLETED. Callers can still optimistically commit
 *     since the right pane shows the loader until the spec arrives.
 *   - `error`: synchronous failure (network error, non-200/202 status).
 *     `error` state is set; callers should NOT optimistically commit.
 */
export type GenerateResult = 'completed' | 'pending' | 'error';

interface UseInsightsPreviewCacheResult {
  /** Sync lookup of a previously-generated spec by settings shape. */
  lookup: (settings: PreviewSettings) => Spec | null;
  /** Triggers generation for the supplied settings. Resolves once the
   * underlying API hop returns. The returned discriminator distinguishes
   * synchronous outcomes so callers can avoid optimistic commits on error. */
  generate: (settings: PreviewSettings) => Promise<GenerateResult>;
  isGenerating: boolean;
  error: string | null;
}

const SAMPLE_PATIENT_SLOT = 'sample';

// Upper bound on how long we'll wait for a 202 row to settle before
// declaring the request stuck. The clock starts at click and resets when
// the 202 response sets `pending.id`, so the effective ceiling is ~60s
// after worker scheduling — enough headroom for normal completion while
// preventing a wedged Generate button on dropped subscriptions or silently
// killed workers.
const PENDING_TIMEOUT_MS = 60_000;

/**
 * Canonical key shape for a preview settings tuple. Exported so callers
 * can detect "settings changed since last commit" without re-deriving
 * the join shape and risking divergence from the cache lookup.
 */
export const previewSettingsKey = (settings: PreviewSettings): string =>
  `${settings.appointmentId ?? SAMPLE_PATIENT_SLOT}|${settings.format}|${settings.detailLevel}|${settings.customInstructions.trim()}`;

type PendingState = {
  id: string | null;
  key: string;
  settings: PreviewSettings;
};

export const useInsightsPreviewCache = ({
  tenantId,
}: UseInsightsPreviewCacheArgs): UseInsightsPreviewCacheResult => {
  const apiClient = useApiClient();

  const cacheRef = useRef<Map<string, Spec>>(new Map());
  // Force re-render when the cache map mutates — `lookup` reads from the
  // ref directly and `useState`-style invalidation isn't required, but a
  // bumping counter keeps consumers' memoised values in sync.
  const [cacheVersion, setCacheVersion] = useState(0);
  const bumpCache = useCallback(() => {
    setCacheVersion((v) => v + 1);
  }, []);

  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingState | null>(null);

  const { data: subscriptionData } = useSubscription(cachedOutputSubscription, {
    variables: { tenantId, id: pending?.id ?? '' },
    skip: !pending?.id,
  });

  useEffect(() => {
    if (!pending?.id || !subscriptionData?.cachedOutputsByPk) return;

    const row = subscriptionData.cachedOutputsByPk;

    if (row.status === WorkerStatusTypesLookup.COMPLETED) {
      const parsed = specSchema.safeParse(row.value);
      if (parsed.success) {
        cacheRef.current.set(pending.key, parsed.data);
        bumpCache();
      } else {
        setError('Preview returned an invalid response. Please try again.');
      }
      setPending(null);
    } else if (row.status === WorkerStatusTypesLookup.FAILED) {
      setError('Preview generation failed. Please try again.');
      setPending(null);
    }
  }, [subscriptionData, pending, bumpCache]);

  // Watchdog: if the subscription never delivers (websocket drop, worker
  // silently killed, etc.), clear `pending` and surface a timeout error
  // so the Generate button doesn't wedge. Depend on `pending !== null`
  // rather than `pending` itself so the 60s ceiling is anchored to the
  // click, not reset every time `pending`'s identity changes (e.g. on
  // the 202 response landing the row id).
  const isPending = pending !== null;
  useEffect(() => {
    if (!isPending) return;
    const timeoutId = window.setTimeout(() => {
      setError('Preview generation timed out. Please try again.');
      setPending(null);
    }, PENDING_TIMEOUT_MS);
    return () => window.clearTimeout(timeoutId);
  }, [isPending]);

  const lookup = useCallback(
    (settings: PreviewSettings): Spec | null => {
      // cacheVersion is intentionally read so memoised lookups invalidate
      // when the cache mutates.
      void cacheVersion;
      return cacheRef.current.get(previewSettingsKey(settings)) ?? null;
    },
    [cacheVersion],
  );

  const generate = useCallback(
    async (settings: PreviewSettings): Promise<GenerateResult> => {
      const requestKey = previewSettingsKey(settings);
      setError(null);
      setPending({ id: null, key: requestKey, settings });

      try {
        const response = await apiClient.insightsPreview.generate({
          params: { tenantId },
          body: {
            format: settings.format,
            detailLevel: settings.detailLevel,
            customInstructions: settings.customInstructions.trim() || null,
            appointmentId: settings.appointmentId,
          },
        });

        if (response.status === 200) {
          cacheRef.current.set(requestKey, response.body.spec);
          bumpCache();
          setPending(null);
          return 'completed';
        } else if (response.status === 202) {
          setPending({
            id: response.body.cachedOutputId,
            key: requestKey,
            settings,
          });
          return 'pending';
        }
        setError('Unable to start preview generation. Please try again.');
        setPending((prev) => (prev?.key === requestKey ? null : prev));
        return 'error';
      } catch {
        setError('Unable to start preview generation. Please try again.');
        setPending((prev) => (prev?.key === requestKey ? null : prev));
        return 'error';
      }
    },
    [apiClient, tenantId, bumpCache],
  );

  return {
    lookup,
    generate,
    isGenerating: pending !== null,
    error,
  };
};
